import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import { FileCheck, AlertTriangle, Loader, Shield, ChevronDown, ChevronRight } from 'lucide-react';

// ─── Types (self-contained — no imports from internal components) ──────────────

interface RunData {
  runNumber: number;
  recordCount: number;
  upfrontCents: number;
  trailCents: number;
  totalCents: number;
}

interface MonthData {
  periodMonth: string;
  runs: RunData[];
}

interface EntityReport {
  entityCode: string;
  legalEntityName: string;
  dateRange: { first: string; last: string };
  totalRecords: number;
  upfrontCents: number;
  trailCents: number;
  totalCents: number;
  months: MonthData[];
}

interface GapReport {
  entityCode: string;
  legalEntityName: string;
  missingMonths: string[];
  sparseRuns: Array<{
    month: string;
    presentRuns: number[];
    typicalRunCount: number;
  }>;
}

interface VerificationReport {
  success: boolean;
  companyId: string;
  entities: EntityReport[];
  gaps: GapReport[];
  grandTotal: {
    records: number;
    upfrontCents: number;
    trailCents: number;
    totalCents: number;
  };
}

interface ShareTokenResponse {
  success: boolean;
  companyId: string;
  companyName: string;
  entities: EntityReport[];
  gaps: GapReport[];
  grandTotal: {
    records: number;
    upfrontCents: number;
    trailCents: number;
    totalCents: number;
  };
  disclaimer: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtAud = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const EntityMonthlyDetail: React.FC<{ entity: EntityReport }> = ({ entity }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-xs">
      <thead>
        <tr className="text-slate-500 uppercase tracking-wider">
          <th className="px-3 py-2 text-left">Month</th>
          <th className="px-3 py-2 text-left">Run</th>
          <th className="px-3 py-2 text-right">Records</th>
          <th className="px-3 py-2 text-right">Upfront</th>
          <th className="px-3 py-2 text-right">Trail</th>
          <th className="px-3 py-2 text-right">Total</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-700/20">
        {entity.months.map((month) =>
          month.runs.map((run, ri) => (
            <tr key={`${month.periodMonth}-${run.runNumber}`} className="hover:bg-slate-800/30">
              <td className="px-3 py-1.5 text-slate-400">{ri === 0 ? month.periodMonth : ''}</td>
              <td className="px-3 py-1.5">
                <span className="px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">R{run.runNumber}</span>
              </td>
              <td className="px-3 py-1.5 text-right text-slate-300">{run.recordCount}</td>
              <td className="px-3 py-1.5 text-right text-green-400/80">{fmtAud(run.upfrontCents)}</td>
              <td className="px-3 py-1.5 text-right text-blue-400/80">{fmtAud(run.trailCents)}</td>
              <td className="px-3 py-1.5 text-right text-slate-200">{fmtAud(run.totalCents)}</td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

const CoverageMatrix: React.FC<{ entities: EntityReport[]; allMonths: string[] }> = ({ entities, allMonths }) => {
  const lookup = useMemo(() => {
    const map = new Map<string, { firstMonth: string; lastMonth: string; monthRuns: Map<string, Set<number>> }>();
    for (const entity of entities) {
      const monthRuns = new Map<string, Set<number>>();
      for (const month of entity.months) {
        const runs = new Set(month.runs.map(r => r.runNumber));
        monthRuns.set(month.periodMonth, runs);
      }
      map.set(entity.entityCode, {
        firstMonth: entity.dateRange.first,
        lastMonth: entity.dateRange.last,
        monthRuns,
      });
    }
    return map;
  }, [entities]);

  return (
    <table className="text-xs">
      <thead>
        <tr>
          <th className="px-2 py-1 text-left text-slate-400 font-normal sticky left-0 bg-slate-800/90 z-10 min-w-[80px]">Entity</th>
          {allMonths.map(m => (
            <th key={m} className="px-1 py-1 text-slate-500 font-normal whitespace-nowrap">{m.substring(2, 7)}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {entities.map(entity => {
          const info = lookup.get(entity.entityCode)!;
          return (
            <tr key={entity.entityCode}>
              <td className="px-2 py-1 text-slate-300 font-mono sticky left-0 bg-slate-800/90 z-10">{entity.entityCode}</td>
              {allMonths.map(month => {
                const isInRange = month >= info.firstMonth && month <= info.lastMonth;
                if (!isInRange) {
                  return <td key={month} className="px-1 py-1 text-center text-slate-700">·</td>;
                }
                const runs = info.monthRuns.get(month);
                if (!runs) {
                  return (
                    <td key={month} className="px-1 py-1 text-center">
                      <span className="inline-block px-1 rounded bg-red-500/20 text-red-400 font-bold">GAP</span>
                    </td>
                  );
                }
                return (
                  <td key={month} className="px-1 py-1 text-center whitespace-nowrap">
                    {[1, 2, 3].map(r => (
                      <span
                        key={r}
                        className={`inline-block w-3 text-center ${runs.has(r) ? 'text-green-400' : 'text-slate-600'}`}
                      >
                        {runs.has(r) ? r : '·'}
                      </span>
                    ))}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const GapCard: React.FC<{ gap: GapReport }> = ({ gap }) => (
  <div className="bg-slate-700/20 rounded-lg p-4 border border-orange-500/20">
    <h5 className="text-sm font-semibold text-white mb-2">
      {gap.entityCode} <span className="text-slate-400 font-normal">({gap.legalEntityName})</span>
    </h5>
    {gap.missingMonths.length > 0 && (
      <div className="mb-2">
        <span className="text-xs text-red-400 uppercase tracking-wider">Missing months: </span>
        <span className="text-sm text-red-300">{gap.missingMonths.join(', ')}</span>
      </div>
    )}
    {gap.sparseRuns.length > 0 && (
      <div>
        <span className="text-xs text-orange-400 uppercase tracking-wider">Sparse runs:</span>
        <div className="mt-1 space-y-0.5">
          {gap.sparseRuns.map(sr => (
            <p key={sr.month} className="text-xs text-orange-300/80">
              {sr.month}: has Run {sr.presentRuns.join(',')} only (typical: {sr.typicalRunCount} runs/month)
            </p>
          ))}
        </div>
      </div>
    )}
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const SharedVerificationReportPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<ShareTokenResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedEntities, setExpandedEntities] = useState<Set<string>>(new Set());

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('No share token provided');
        setIsLoading(false);
        return;
      }

      try {
        const validateFunc = httpsCallable<
          { token: string },
          ShareTokenResponse
        >(functions, 'validateVerificationShareToken');

        const response = await validateFunc({ token });
        setData(response.data);
      } catch (err: any) {
        console.error('Failed to validate verification share token:', err);

        if (err.code === 'functions/permission-denied') {
          setError('This share link is invalid or has expired');
        } else if (err.code === 'functions/not-found') {
          setError('Verification report not found');
        } else if (err.code === 'functions/failed-precondition') {
          setError(err.message || 'Report is not available');
        } else {
          setError(err.message || 'Failed to load shared verification report');
        }
      } finally {
        setIsLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const allMonths = useMemo(() => {
    if (!data?.entities) return [];
    const months = new Set<string>();
    for (const entity of data.entities) {
      for (const m of entity.months) {
        months.add(m.periodMonth);
      }
    }
    return Array.from(months).sort();
  }, [data]);

  const toggleEntity = (code: string) => {
    setExpandedEntities(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  // ─── Loading state ──────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-blue-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-300 text-lg">Validating share link...</p>
          <p className="text-slate-500 text-sm mt-1">Loading verification report</p>
        </div>
      </div>
    );
  }

  // ─── Error state ────────────────────────────────────────────────────────────

  if (error || !data || !data.entities) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-8">
        <div className="max-w-md w-full bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-red-500/30 text-center">
          <div className="p-4 bg-red-500/20 rounded-full w-fit mx-auto mb-4">
            <AlertTriangle className="h-10 w-10 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Unable to Load Report</h1>
          <p className="text-slate-400 mb-6">{error || 'Verification report data is unavailable'}</p>
          <p className="text-xs text-slate-600">
            If you believe this is an error, please contact the person who shared this link.
          </p>
        </div>
      </div>
    );
  }

  // ─── Success state ──────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header with VALUTTEN branding */}
        <div className="mb-8">
          <div className="mb-6">
            <img src="/valutten-logo.jpg" alt="VALUTTEN" className="h-10 w-auto" />
          </div>

          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <FileCheck className="w-8 h-8 text-blue-400" />
            Upload Verification Report
          </h1>
          <p className="text-lg text-slate-300">
            Prepared for: <span className="text-white font-semibold">{data.companyName}</span>
          </p>
          <p className="text-sm text-slate-500 mt-1">
            Shared Report — Read Only
          </p>
        </div>

        {/* Disclaimer banner */}
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-8">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-300">
              <p>
                This report was generated by VALUTTEN on behalf of{' '}
                <span className="font-semibold text-blue-200">{data.companyName}</span> under our Terms
                of Service (Section 6.3 — support and troubleshooting). Data is confidential and intended
                solely for the named recipient.
              </p>
              <p className="mt-2 text-blue-400/80">
                Do not forward.
              </p>
            </div>
          </div>
        </div>

        {/* Grand Total Cards */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-slate-700/30 rounded-lg p-4">
              <p className="text-sm text-slate-400">Total Records</p>
              <p className="text-2xl font-bold text-white">{data.grandTotal.records.toLocaleString()}</p>
            </div>
            <div className="bg-slate-700/30 rounded-lg p-4">
              <p className="text-sm text-slate-400">Upfront</p>
              <p className="text-2xl font-bold text-green-400">{fmtAud(data.grandTotal.upfrontCents)}</p>
            </div>
            <div className="bg-slate-700/30 rounded-lg p-4">
              <p className="text-sm text-slate-400">Trail</p>
              <p className="text-2xl font-bold text-blue-400">{fmtAud(data.grandTotal.trailCents)}</p>
            </div>
            <div className="bg-slate-700/30 rounded-lg p-4">
              <p className="text-sm text-slate-400">Total Commission</p>
              <p className="text-2xl font-bold text-white">{fmtAud(data.grandTotal.totalCents)}</p>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-3">
            {data.entities.length} entities | {allMonths.length} months
            {allMonths.length > 0 && ` (${allMonths[0]} to ${allMonths[allMonths.length - 1]})`}
          </p>
        </div>

        {/* Entity Summary Table */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden mb-6">
          <div className="p-6 border-b border-slate-700/50">
            <h4 className="text-lg font-semibold text-white">Entity Summary</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs text-slate-400 uppercase tracking-wider border-b border-slate-700/50">
                  <th className="px-6 py-3 w-8"></th>
                  <th className="px-4 py-3">Entity</th>
                  <th className="px-4 py-3">Legal Name</th>
                  <th className="px-4 py-3 text-right">Records</th>
                  <th className="px-4 py-3 text-right">Upfront</th>
                  <th className="px-4 py-3 text-right">Trail</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3">Range</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/30">
                {data.entities.map((entity) => (
                  <React.Fragment key={entity.entityCode}>
                    <tr
                      className="hover:bg-slate-700/20 transition-colors cursor-pointer"
                      onClick={() => toggleEntity(entity.entityCode)}
                    >
                      <td className="px-6 py-3">
                        {expandedEntities.has(entity.entityCode)
                          ? <ChevronDown className="w-4 h-4 text-slate-400" />
                          : <ChevronRight className="w-4 h-4 text-slate-400" />
                        }
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-white">{entity.entityCode}</td>
                      <td className="px-4 py-3 text-sm text-slate-300">{entity.legalEntityName}</td>
                      <td className="px-4 py-3 text-sm text-slate-300 text-right">{entity.totalRecords.toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-green-400 text-right">{fmtAud(entity.upfrontCents)}</td>
                      <td className="px-4 py-3 text-sm text-blue-400 text-right">{fmtAud(entity.trailCents)}</td>
                      <td className="px-4 py-3 text-sm text-white text-right font-medium">{fmtAud(entity.totalCents)}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{entity.dateRange.first} → {entity.dateRange.last}</td>
                    </tr>
                    {expandedEntities.has(entity.entityCode) && (
                      <tr>
                        <td colSpan={8} className="px-6 py-4 bg-slate-900/30">
                          <EntityMonthlyDetail entity={entity} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Coverage Matrix */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden mb-6">
          <div className="p-6 border-b border-slate-700/50">
            <h4 className="text-lg font-semibold text-white">Monthly Coverage Matrix</h4>
            <p className="text-xs text-slate-500 mt-1">Green = run present | Red = missing within active range | Gray = outside range</p>
          </div>
          <div className="overflow-x-auto p-4">
            <CoverageMatrix entities={data.entities} allMonths={allMonths} />
          </div>
        </div>

        {/* Gap Analysis */}
        {data.gaps.length > 0 && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-orange-500/30 overflow-hidden mb-8">
            <div className="p-6 border-b border-slate-700/50">
              <h4 className="text-lg font-semibold text-orange-400 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Gap Analysis — {data.gaps.length} entit{data.gaps.length === 1 ? 'y' : 'ies'} with gaps
              </h4>
            </div>
            <div className="p-6 space-y-4">
              {data.gaps.map((gap) => (
                <GapCard key={gap.entityCode} gap={gap} />
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center py-6 text-slate-500 text-sm border-t border-slate-700/50 mt-8">
          <p>This is a shared, read-only view of the verification report.</p>
          <p className="mt-2">
            <a href="https://valutten.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 font-medium">
              Powered by VALUTTEN
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SharedVerificationReportPage;
